/**
 * Splitting a run's workspace into what the reader came for and what the run
 * happened to write.
 *
 * Here rather than in the component because it is the part with an opinion in
 * it — which file is the product, which is a specialist's working paper — and
 * that opinion is worth a test. The component below it only draws arrows.
 */

import { humanise } from "./format";
import type { Artefact, RunTask } from "./types";

/**
 * The run's own record, as against the playbook's product.
 *
 * Rendered from the ledger by `_finalise` for every playbook, so they are the
 * two filenames that are the same everywhere — which is exactly why they can
 * be named here and the deliverable cannot.
 */
export const RUN_RECORD = ["evidence_log.md", "citation_report.md"];

/**
 * Where a specialist writes: `agents/<its-name>/`, enforced by the write guard
 * rather than agreed by convention, which is what makes grouping on the prefix
 * reliable instead of a guess about filenames.
 */
export const AGENT_PREFIX = "agents/";

/**
 * The board summary's file. A deliverable in its own right — eight messages a
 * board reads instead of the report — so it is listed with the deliverables
 * rather than among the working files, where it landed out of sight (#129).
 */
export const BOARD_SUMMARY = "summary.md";

/** One heading in the file list, and everything filed under it. */
export interface Group {
  key: string;
  title: string;
  hint?: string;
  files: Artefact[];
  /**
   * Open on arrival. Only the deliverables and the evidence are — the rest is
   * working papers, and the complaint this view answers is that they crowded
   * out the product.
   */
  open: boolean;
}

export function stemOf(name: string): string {
  return name.replace(/\.(md|docx|html)$/i, "");
}

/**
 * The formats only a deliverable builder writes.
 *
 * **`declared_deliverables` cannot ever name one of these, and that is the
 * whole of #91.** The loader refuses a deliverable that is not markdown —
 * "only .md is published" — because the field's job is to tell `_publish`
 * what to parse and convert. So the manifest's list is markdown by
 * construction, and a membership test of "its stem matches something
 * declared" loses every workbook and every deck, on every playbook, always.
 * `bottom_up_market_model`'s own `lead.md` calls its `.xlsx` a deliverable in
 * prose and its `playbook.yaml` is not allowed to say so.
 *
 * `stemOf` is right for what it does: `.md`, `.html` and `.docx` are one
 * document three ways. A workbook is a second product, not a third format of
 * the first — which is why this is a suffix rule rather than another suffix in
 * `stemOf`. A benchmarking run writes `peer_financials.xlsx` beside a report
 * named nothing like it, and a stem rule would file that under phase
 * transcripts too.
 *
 * Nothing else in a workspace is one of these: a phase transcript is `.md`, a
 * specialist writes under `agents/`, an image is an image.
 */
export const PRODUCT_FORMATS = /\.(xlsx|xlsm|pptx)$/i;

export function leafOf(name: string): string {
  return name.slice(name.lastIndexOf("/") + 1);
}

/**
 * `declared_deliverables` comes from the run's own manifest, so this is the
 * playbook's answer to "what is the product here" rather than a list of
 * filenames held in the client. The previous version ranked `report.md` first,
 * which is `cdd`'s deliverable and nobody else's — so on a company profile or
 * a market model the actual product sorted in among the phase transcripts,
 * which is the report behind this.
 *
 * Older runs have no `declared_deliverables` in their manifest. They fall back
 * to "anything at the top level that is not a working file", which is what the
 * old ranking approximated and is still true of them.
 */
export function group(artefacts: Artefact[], declared: string[]): Group[] {
  const stems = new Set(declared.map(stemOf));
  const isDeliverable = (name: string) =>
    declared.length > 0
      ? // Its published .html and .docx travel with it: they are the same
        // document, and splitting them would put two thirds of the product
        // under "other files".
        stems.has(stemOf(name)) ||
        name === BOARD_SUMMARY ||
        // A workbook or a deck is a product in its own right. Top level only,
        // which is where a builder writes: a specialist's files live under
        // `agents/` and are grouped by author instead.
        (!name.includes("/") && PRODUCT_FORMATS.test(name))
      : !name.includes("/") && !name.startsWith("_") && !RUN_RECORD.includes(name);

  const deliverables: Artefact[] = [];
  const record: Artefact[] = [];
  const bySpecialist = new Map<string, Artefact[]>();
  const images: Artefact[] = [];
  const working: Artefact[] = [];

  for (const file of artefacts) {
    if (isDeliverable(file.name)) deliverables.push(file);
    else if (RUN_RECORD.includes(file.name)) record.push(file);
    else if (file.name.startsWith(AGENT_PREFIX)) {
      const name = file.name.slice(AGENT_PREFIX.length).split("/")[0] ?? "";
      const bucket = bySpecialist.get(name) ?? [];
      bucket.push(file);
      bySpecialist.set(name, bucket);
    } else if (file.media_type.startsWith("image/")) images.push(file);
    else working.push(file);
  }

  const groups: Group[] = [];
  if (deliverables.length)
    groups.push({
      key: "deliverables",
      title: "Deliverables",
      files: deliverables,
      open: true,
    });
  if (record.length)
    groups.push({
      key: "record",
      title: "Evidence",
      hint: "Rendered from the ledger, not written by the agent",
      files: record,
      open: true,
    });
  for (const [name, files] of [...bySpecialist].sort())
    groups.push({
      key: `agent:${name}`,
      // "Competitor analyst", not COMPETITOR-ANALYST: the folder name is the
      // write guard's key, and the reader wants the role (#124).
      title: humanise(name),
      hint: "Specialist",
      files,
      open: false,
    });
  if (images.length)
    groups.push({ key: "images", title: "Images", files: images, open: false });
  if (working.length)
    groups.push({
      key: "working",
      title: "Working files",
      hint: "Phase transcripts and intermediates",
      files: working,
      open: false,
    });
  return groups;
}

/**
 * The deck a finished build wrote, once the file listing has caught up.
 *
 * Two different reads answer "is the deck there": `task.decks` is what the
 * builder said it wrote, and the artefact listing is what the server can
 * serve. The second one lags — the deck is written after the run finished, so
 * nothing was polling the listing while it built. Selecting a name the list
 * does not hold yet would put the preview on a file the file list denies
 * having, which is the failure `holdsActive` exists to stop one level down.
 *
 * So this returns null until both agree, and the caller waits. A build that
 * wrote nothing — `decks` empty, the model declined, the check refused —
 * returns null for ever, and the reader keeps the document they were reading
 * plus the line saying no deck was written.
 */
export function builtDeck(
  task: RunTask | null | undefined,
  artefacts: Artefact[],
): string | null {
  if (!task || task.kind !== "deck" || task.status !== "completed") return null;
  const listed = new Set(artefacts.map((a) => a.name));
  return (task.decks ?? []).find((name) => listed.has(name)) ?? null;
}

/**
 * The board summary a finished task wrote, once the listing holds it — the
 * same two reads `builtDeck` waits on, for the same reason.
 */
export function builtSummary(
  task: RunTask | null | undefined,
  artefacts: Artefact[],
): string | null {
  if (!task || task.kind !== "summary" || task.status !== "completed" || !task.file) {
    return null;
  }
  return artefacts.some((a) => a.name === task.file) ? task.file : null;
}

/**
 * The file the Deliverables tab opens on.
 *
 * It was `ordered[0]` — the first file of the first group — and the
 * deliverables group sorts by name, so a run whose deck was called
 * `acme-commercial-due-diligence.pptx` opened on the deck's outline rather
 * than the report the playbook declared (#124). The playbook's own answer
 * comes first: the first declared deliverable that is present, as markdown,
 * because that is the text the other formats are renderings of.
 */
export function primaryFile(artefacts: Artefact[], declared: string[]): string | null {
  const names = new Set(artefacts.map((a) => a.name));
  for (const name of declared) if (names.has(name)) return name;
  const groups = group(artefacts, declared);
  const deliverables = groups.find((g) => g.key === "deliverables")?.files ?? [];
  const markdown = deliverables.find((f) => f.name.toLowerCase().endsWith(".md"));
  return markdown?.name ?? groups[0]?.files[0]?.name ?? null;
}
